import { LoanTypeOption } from "@/components/calculator/utils/types";

/**
 * Get default values for specific loan types
 * @param loanType The type of loan
 * @param isVeteran Whether the user is a veteran (relevant for VA loans)
 * @returns Object containing default values for the specified loan type
 */
export const getDefaultValuesForLoanType = (loanType: LoanTypeOption, isVeteran: boolean) => {
  switch (loanType) {
    case "conventional":
      return {
        // Default values for conventional loans
        downPaymentPercentage: 20,
        interestRate: 6.5,
        loanTerm: 30
      };
    case "fha":
      return {
        // Default values for FHA loans
        downPaymentPercentage: 3.5,
        interestRate: 6.75,
        loanTerm: 30,
        creditScore: 640, // FHA typically requires 580+ for 3.5% down
        cashReserves: 2, // 2 months of reserves
        currentHousingPayment: 0,
        residualIncome: false
      };
    case "va":
      return {
        // Default values for VA loans
        downPaymentPercentage: 0,
        interestRate: 6.25,
        loanTerm: 30,
        vaStatus: isVeteran ? "separatedRetired" as const : null,
        homebuyingHistory: "firstTime" as const,
        vaDisabilityStatus: "none" as const
      };
    case "usda":
      return {
        // Default values for USDA loans
        downPaymentPercentage: 0,
        interestRate: 6.35,
        loanTerm: 30
      };
    default:
      return {
        downPaymentPercentage: 20,
        interestRate: 6.5,
        loanTerm: 30
      };
  }
};

/**
 * Creates a type-safe update object from default values
 * @param defaultValues The default values object
 * @returns A type-safe partial update object
 */
export const createTypeSafeDefaultValues = (defaultValues: ReturnType<typeof getDefaultValuesForLoanType>, mortgageData: any) => {
  const typeSafeDefaultValues: Partial<typeof mortgageData> = {};
  
  // Copy each property from defaultValues, ensuring they match the expected types
  if ('downPaymentPercentage' in defaultValues) {
    typeSafeDefaultValues.downPaymentPercentage = defaultValues.downPaymentPercentage;
    // Also calculate the down payment amount based on the percentage
    if (mortgageData.homePrice) {
      typeSafeDefaultValues.downPayment = Math.round((defaultValues.downPaymentPercentage / 100) * mortgageData.homePrice);
    }
  }
  
  if ('interestRate' in defaultValues) typeSafeDefaultValues.interestRate = defaultValues.interestRate;
  if ('loanTerm' in defaultValues) typeSafeDefaultValues.loanTerm = defaultValues.loanTerm;
  if ('vaStatus' in defaultValues) {
    typeSafeDefaultValues.vaStatus = defaultValues.vaStatus as "activeDuty" | "separatedRetired" | null;
  }
  if ('homebuyingHistory' in defaultValues) {
    typeSafeDefaultValues.homebuyingHistory = defaultValues.homebuyingHistory as "firstTime" | "purchasedLastThreeYears" | "other" | null;
  }
  if ('vaDisabilityStatus' in defaultValues) {
    typeSafeDefaultValues.vaDisabilityStatus = defaultValues.vaDisabilityStatus as "none" | "partial" | "full" | null;
  }
  
  return typeSafeDefaultValues;
};
